import {
  Fragment,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import ModalOverlay, {
  ModalOverlayContent,
  ModalOverlayFooter,
} from '../shared/overlays/modal-overlay'
import Label from './label'
import Input from './input'
import Button from './button'
import { Combobox as UiCombobox } from '@headlessui/react'
import ValidationMessage from '../shared/validation-message'

type OptionTypeBase = { label: string }

type CreateOptions<OptionType extends OptionTypeBase> = {
  modalTitle: string
  inputLabel: string
  inputPlaceholder?: string
  onCreate: (label: string) => OptionType
}

type ComboboxProps<OptionType extends OptionTypeBase> = {
  createNew?: CreateOptions<OptionType>
  value: OptionType | null
  className?: string
  options: OptionType[]
  placeholder?: string
  disabled?: boolean
  onChange: (value: OptionType) => void
  onQueryChange?: (query: string) => void
}

export default function Combobox<OptionType extends OptionTypeBase>({
  value,
  options,
  onChange,
  onQueryChange,
  className,
  disabled,
  placeholder,
}: ComboboxProps<OptionType>) {
  const [query, setQuery] = useState('')
  const filteredOptions = useMemo(() => {
    const lowercaseQuery = query.toLowerCase()
    return query === '' || query === value?.label
      ? options
      : options.filter(o => o.label.toLowerCase().includes(lowercaseQuery))
  }, [options, query, value?.label])
  const [forceOpen, setForceOpen] = useState(false)
  useEffect(() => {
    if (value?.label) {
      setQuery(value.label)
    }
  }, [value?.label])
  useEffect(() => {
    if (onQueryChange) {
      onQueryChange(query)
    }
  }, [onQueryChange, query])

  return (
    <div className="tw-relative">
      <UiCombobox
        value={value}
        disabled={disabled}
        onChange={value => {
          if (value != null) {
            setForceOpen(false)
            onChange(value)
          }
        }}>
        {({ open }) => (
          <>
            <UiCombobox.Input
              placeholder={placeholder}
              aria-disabled={disabled}
              className={`
                ${className}
                tw-block
                tw-border
                tw-border-solid
                tw-w-full
                tw-py-2
                tw-px-2
                tw-leading-none
                tw-rounded
                tw-shadow
                ${
                  !disabled
                    ? 'tw-border-system-grey-300 hover:tw-border-system-grey-400 tw-cursor-pointer'
                    : 'tw-cursor-not-allowed tw-text-system-grey-400'
                }
                focus:tw-outline-none
                focus:tw-ring
                `}
              onClick={() => {
                setForceOpen(true)
              }}
              onBlur={() => {
                // Without timeout / too short timeout, it appears the blur
                // will close the options dropdown before a click event is registered when
                // the user selects an option from the dropdown.
                // (I have not fully understood if there's something more going
                // on here, 200ms seems like a *really* long time to work around this,
                // but with 100ms, it's sometimes(!) impossible to select a new value ?!)
                setTimeout(() => setForceOpen(false), 200)
              }}
              displayValue={(value: OptionType | undefined) =>
                value?.label || ''
              }
              onChange={e => setQuery(e.target.value)}
            />
            <UiCombobox.Button className="tw-pointer-events-none tw-absolute tw-top-2 tw-right-0 tw-flex tw-items-center tw-px-3 tw-text-system-grey-300 tw-text-xl">
              <i className="tw-fill-current tw-h-4 tw-w-4 fas fa-angle-down"></i>
            </UiCombobox.Button>

            {(open || forceOpen) && options.length > 0 ? (
              <UiCombobox.Options
                className="tw-absolute tw-z-10 tw-max-h-60 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-white tw-py-1 tw-text-base tw-shadow-lg tw-ring-1 tw-ring-black/5 focus:tw-outline-none"
                static>
                {filteredOptions.map(option => (
                  <UiCombobox.Option
                    key={option.label}
                    value={option}
                    as={Fragment}>
                    {({ active, selected }) => (
                      <li
                        className={`tw-group tw-cursor-default tw-select-none tw-py-2 tw-px-2 tw-text-sm tw-text-nowrap tw-truncate ${
                          active ? 'tw-bg-system-grey-100' : ''
                        }`}>
                        <div className="tw-inline-block tw-w-4">
                          {selected && <i className="fas fa-check" />}
                        </div>
                        {option.label}
                      </li>
                    )}
                  </UiCombobox.Option>
                ))}
              </UiCombobox.Options>
            ) : null}
          </>
        )}
      </UiCombobox>
    </div>
  )
}

export function ComboboxWithCreate<OptionType extends OptionTypeBase>({
  createNew,
  options,
  onChange,
  disabled,
  ...props
}: ComboboxProps<OptionType>) {
  const [showModal, setShowModal] = useState(false)
  const [query, setQuery] = useState('')

  return (
    <>
      <div className="tw-flex tw-gap-x-2 tw-justify-between">
        <div className="tw-flex-grow">
          <Combobox
            options={options}
            disabled={disabled}
            onChange={onChange}
            onQueryChange={setQuery}
            {...props}
          />
        </div>
        {createNew ? (
          <Button
            className="tw-flex-shrink-0"
            disabled={disabled}
            onClick={() => setShowModal(true)}>
            <i className="fas fa-plus" /> New
          </Button>
        ) : null}
      </div>
      {createNew && showModal && (
        <CreateModal
          initialValue={query}
          options={createNew}
          onDismiss={value => {
            if (value) {
              onChange(value)
            }
            setShowModal(false)
          }}
        />
      )}
    </>
  )
}

function CreateModal<OptionType extends OptionTypeBase>({
  options: { modalTitle, inputLabel, inputPlaceholder, onCreate },
  initialValue,
  onDismiss,
}: {
  options: CreateOptions<OptionType>
  initialValue: string
  onDismiss: (value?: OptionType) => void
}) {
  const [label, setLabel] = useState(initialValue)
  const [touched, setTouched] = useState(false)
  useEffect(() => {
    window.addEventListener('keypress', listener)
    return () => {
      window.removeEventListener('keypress', listener)
    }

    function listener(e: KeyboardEvent) {
      if (e.code === 'Enter') {
        onDismiss(onCreate(label))
      }
    }
  }, [label, onCreate, onDismiss])
  const inputRef: MutableRefObject<HTMLInputElement | null> = useRef(null)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])
  const isValid = label.length > 0

  return (
    <ModalOverlay show={true} title={modalTitle} onDismiss={() => onDismiss()}>
      <ModalOverlayContent>
        <Label>{inputLabel}</Label>
        <Input
          ref={inputRef}
          type="text"
          value={label}
          onChange={e => {
            setLabel(e.target.value)
            setTouched(true)
          }}
          placeholder={inputPlaceholder}
        />
        <ValidationMessage>
          {touched && !isValid && <>{inputLabel} can&apos;t be empty</>}
        </ValidationMessage>
      </ModalOverlayContent>
      <ModalOverlayFooter>
        <div className="tw-flex tw-justify-between tw-gap-x-2">
          <Button
            size="large"
            className="tw-w-auto"
            onClick={() => onDismiss()}>
            Cancel
          </Button>
          <Button
            size="large"
            className="tw-w-auto"
            action="primary"
            disabled={!isValid}
            onClick={() => onDismiss(onCreate(label))}>
            Create
          </Button>
        </div>
      </ModalOverlayFooter>
    </ModalOverlay>
  )
}
